import { useCallback, useEffect, useRef, useState } from "react";

import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { trackModalClosed, trackModalOpened, trackSheetClosed, trackSheetOpened } from "@/lib/analytics/events";
import type { OverlayCloseReason } from "@/lib/analytics/types";

type OverlayKind = "modal" | "sheet";

export type OverlayAnalyticsParams = {
  /** Structured ID of the overlay; omit to disable emission entirely. */
  analyticsElementId?: AnalyticsElementId;
  open: boolean;
  /**
   * True while the overlay shows a failed action outcome (e.g. a mutation error message). A
   * failure re-arms the close reason to `dismiss`, so abandoning a failed confirm/submit is
   * never counted as a successful-outcome close.
   */
  actionFailed?: boolean;
};

// The kind picks the helper at call time rather than at import time, so a test that mocks
// `@/lib/analytics/events` with only one pair of helpers keeps working for its own kind.
const trackOpened = (kind: OverlayKind, elementId: AnalyticsElementId) =>
  kind === "modal" ? trackModalOpened({ elementId }) : trackSheetOpened({ elementId });

const trackClosed = (kind: OverlayKind, elementId: AnalyticsElementId, reason: OverlayCloseReason) =>
  kind === "modal" ? trackModalClosed({ elementId, reason }) : trackSheetClosed({ elementId, reason });

/**
 * The lifecycle behind `useModalAnalytics` and `useSheetAnalytics`: emits `fe_<kind>_opened` /
 * `fe_<kind>_closed` around a controlled overlay's `open` state. The close reason defaults to
 * `dismiss` (escape / overlay / X); the owner reports an explicit outcome by calling
 * `setCloseReason` from its confirm/submit/cancel handlers before the close propagates. No-ops
 * without an `analyticsElementId`. Use one of the two public hooks, not this directly.
 */
export function useOverlayAnalytics(
  kind: OverlayKind,
  { analyticsElementId, open, actionFailed = false }: OverlayAnalyticsParams,
) {
  const closeReasonRef = useRef<OverlayCloseReason>("dismiss");
  const previousOpenRef = useRef(false);
  const [dismissalRequest, setDismissalRequest] = useState(0);
  const pendingDismissalRef = useRef(false);

  // Keep the latest id available to the unmount cleanup without making it rerun mid-open.
  const analyticsIdRef = useRef(analyticsElementId);
  analyticsIdRef.current = analyticsElementId;

  // The id the open event was emitted under, held until this overlay reports its close.
  //
  // An overlay whose id derives from the state that opened it — `confirm-${pendingAction}-modal`
  // — has that state cleared by the very handler that closes it. Both land in one render, and
  // the id would have already moved on by the time the close effect runs: opened as
  // `confirm-takedown-modal`, closed as `confirm-none-modal`, leaving the funnel with an
  // unmatched open and a close nobody opened. Latching keeps a close on the id it opened with.
  const openedIdRef = useRef<AnalyticsElementId | undefined>(undefined);

  const emitClose = useCallback(() => {
    const elementId = openedIdRef.current;
    previousOpenRef.current = false;
    openedIdRef.current = undefined;
    if (!elementId) return;
    trackClosed(kind, elementId, closeReasonRef.current);
  }, [kind]);

  useEffect(() => {
    // A local state update guarantees a render even if the caller rejects a pending close.
    // Only a dismissal accepted in that render may replace the latched action outcome.
    const dismissed = pendingDismissalRef.current;
    pendingDismissalRef.current = false;
    if (open === previousOpenRef.current) {
      return;
    }

    if (open) {
      previousOpenRef.current = true;
      closeReasonRef.current = "dismiss";
      const elementId = analyticsIdRef.current;
      openedIdRef.current = elementId;
      if (elementId) trackOpened(kind, elementId);
    } else {
      if (dismissed) closeReasonRef.current = "dismiss";
      emitClose();
    }
  }, [open, kind, emitClose, dismissalRequest]);

  // A failed action outcome (overlay stays open showing the error) invalidates any previously
  // latched confirm/submit reason — a later escape/overlay close is a dismissal.
  useEffect(() => {
    if (actionFailed) {
      closeReasonRef.current = "dismiss";
    }
  }, [actionFailed]);

  // Conditionally-mounted overlays unmount instead of flipping `open` to false — close the
  // funnel on true unmount so every opened overlay still reports a close.
  useEffect(() => {
    return () => {
      if (previousOpenRef.current) {
        // Some owners accept dismissal by unmounting instead of rendering open=false.
        if (pendingDismissalRef.current) closeReasonRef.current = "dismiss";
        emitClose();
      }
    };
  }, [emitClose]);

  const setCloseReason = useCallback((reason: OverlayCloseReason) => {
    closeReasonRef.current = reason;
  }, []);

  /** Call alongside the controlled onOpenChange(false); rejected requests retain the action outcome. */
  const requestDismiss = useCallback(() => {
    pendingDismissalRef.current = true;
    setDismissalRequest((request) => request + 1);
  }, []);

  return { setCloseReason, requestDismiss };
}
